import { useState, type FormEvent } from 'react';
import { EllipsisVertical, Plus, CircleX } from 'lucide-react';
import { useLossReasons, type LossReason } from '@/hooks/useLossReasons';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import ScrollArea from '@/components/ui/shared/ScrollArea';
import { SettingsCard } from './SettingsCard';
import { useTranslation } from "react-i18next";

const EMPTY_FORM = {
  name: '',
  is_active: true,
};

const inputClassName =
  'h-11 min-h-[36px] rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus-visible:border-[#2563EB] focus-visible:ring-[#2563EB]/20 dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] dark:focus-visible:border-[#60A5FA] dark:focus-visible:ring-[#2563EB]/30';

const LossReasonsTab = () => {
  const { t } = useTranslation("settings");
  const {
    lossReasons,
    loading,
    error,
    createLossReason,
    updateLossReason,
    deleteLossReason,
  } = useLossReasons({ includeInactive: true });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReason, setEditingReason] = useState<LossReason | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setEditingReason(null);
    setFormData(EMPTY_FORM);
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const handleEdit = (reason: LossReason) => {
    setEditingReason(reason);
    setFormData({
      name: reason.name,
      is_active: reason.is_active,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const name = formData.name.trim();
    if (!name) return;

    setSaving(true);
    const success = editingReason
      ? await updateLossReason(editingReason.id, { name, is_active: formData.is_active })
      : Boolean(await createLossReason(name));
    setSaving(false);

    if (success) {
      setDialogOpen(false);
      resetForm();
    }
  };

  const handleDeactivate = async (reason: LossReason) => {
    if (window.confirm(t("lossReasons.confirmDeactivate", { name: reason.name }))) {
      await deleteLossReason(reason.id);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h2 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("lossReasons.title")}</h2>
          <p className="text-sm text-[#64748B] dark:text-[#71717A]">
            {t("lossReasons.subtitle")}
          </p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
          <DialogTrigger asChild>
            <Button className="rounded-xl bg-[#2563EB] px-4 text-sm text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8] dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]">
              <Plus className="w-4 h-4 mr-2" />
              {t("lossReasons.new")}
            </Button>
          </DialogTrigger>
          <DialogContent className="flex w-full p-0 rounded-2xl bg-white border border-[#E5E7EB] text-[#0F172A] dark:bg-[#27272A] dark:border-[#3F3F46] dark:text-[#FAFAFA] flex-col max-w-[500px]">
            <ScrollArea classNames={{ root: 'max-h-[90dvh] p-5 overflow-auto' }}>
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                  {editingReason ? 'Editar motivo de perda' : 'Novo motivo de perda'}
                </DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="w-full mt-4 gap-4 flex flex-col">
                <div className="flex flex-col w-full items-start gap-3">
                  <Label htmlFor="loss-reason-name" className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">
                    {t("lossReasons.reasonName")}
                  </Label>
                  <Input
                    id="loss-reason-name"
                    value={formData.name}
                    onChange={(event) => setFormData(current => ({
                      ...current,
                      name: event.target.value,
                    }))}
                    placeholder={t("lossReasons.namePlaceholder")}
                    className={inputClassName}
                    autoFocus
                    required
                  />
                </div>

                {editingReason ? (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="loss-reason-active"
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData(current => ({
                        ...current,
                        is_active: checked === true,
                      }))}
                    />
                    <Label
                      htmlFor="loss-reason-active"
                      className="cursor-pointer text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]"
                    >
                      {t("lossReasons.activeReason")}
                    </Label>
                  </div>
                ) : null}

                <div className="flex gap-2 justify-end mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    className="rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#334155] transition-colors duration-200 hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving || !formData.name.trim()}
                    className="rounded-xl bg-[#2563EB] text-sm text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8] disabled:bg-[#E5E7EB] disabled:text-[#94A3B8] dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8] dark:disabled:bg-[#3F3F46] dark:disabled:text-[#71717A]"
                  >
                    {saving ? 'Salvando...' : editingReason ? 'Salvar' : 'Criar'}
                  </Button>
                </div>
              </form>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>

      {error ? (
        <p
          role="alert"
          className="rounded-xl border border-red-200 bg-[#FEF2F2] p-3 text-sm text-[#DC2626] dark:border-red-500/30 dark:bg-red-500/10 dark:text-[#F87171]"
        >
          {t("lossReasons.operationFailed", { error })}
        </p>
      ) : null}

      <SettingsCard>
        {loading ? (
          <div className="divide-y divide-[#E5E7EB] dark:divide-[#3F3F464D]" aria-busy="true" aria-label={t("lossReasons.loadingAria")}>
            {[0, 1, 2].map((i) => (
              <div key={i} className="p-4 flex items-center justify-between">
                <div className="h-4 w-40 rounded bg-[#F1F5F9] animate-pulse dark:bg-[#3F3F46]" />
                <div className="h-[34px] w-[34px] rounded-lg bg-[#F1F5F9] animate-pulse dark:bg-[#3F3F46]" />
              </div>
            ))}
          </div>
        ) : lossReasons.length === 0 ? (
          <div className="p-10 flex flex-col items-center text-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/15 dark:text-[#60A5FA]">
              <CircleX className="h-5 w-5" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA]">{t("lossReasons.empty")}</p>
              <p className="text-sm text-[#64748B] dark:text-[#71717A] mt-1">
                {t("lossReasons.emptyHint")}
              </p>
            </div>
            <Button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="mt-2 rounded-xl bg-[#2563EB] px-4 text-sm text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8]"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("lossReasons.new")}
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-[#E5E7EB] dark:divide-[#3F3F464D]">
            {lossReasons.map(reason => (
              <div
                key={reason.id}
                className="p-4 flex items-center justify-between bg-white transition-colors duration-200 hover:bg-[#F8FAFC] dark:bg-transparent dark:hover:bg-[#3F3F46]"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-medium text-[#0F172A] dark:text-[#FAFAFA] truncate">{reason.name}</h3>
                    {!reason.is_active ? (
                      <span className="text-xs px-2 py-1 rounded-full bg-[#FEF2F2] text-[#DC2626] dark:bg-red-500/15 dark:text-[#F87171]">
                        {t("common.inactive")}
                      </span>
                    ) : null}
                  </div>
                </div>

                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label={t('lossReasons.openActions', { name: reason.name })}
                      className="flex justify-center items-center rounded-lg border border-[#E5E7EB] bg-white w-[34px] h-[34px] text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:border-[#3F3F46] dark:bg-transparent dark:text-[#A1A1AA] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]"
                    >
                      <EllipsisVertical size={18} className="text-current" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="!bg-white p-1 w-[110px] flex flex-col gap-1 border border-[#E5E7EB] rounded-xl shadow-md dark:!bg-[#27272A] dark:border-[#3F3F46] dark:shadow-none">
                    <button
                      type="button"
                      onClick={() => handleEdit(reason)}
                      className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#F1F5F9] min-h-[29px] text-sm text-[#0F172A] transition-colors duration-200 dark:hover:bg-[#3F3F46] dark:text-[#FAFAFA]"
                    >
                      {t("common.edit")}
                    </button>
                    {reason.is_active ? (
                      <button
                        type="button"
                        onClick={() => handleDeactivate(reason)}
                        className="w-full flex justify-center rounded-lg items-center bg-transparent hover:bg-[#FEF2F2] min-h-[29px] text-sm text-[#DC2626] transition-colors duration-200 dark:hover:bg-red-500/10 dark:text-[#F87171]"
                      >
                        {t("lossReasons.deactivate")}
                      </button>
                    ) : null}
                  </PopoverContent>
                </Popover>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>
    </div>
  );
};

export default LossReasonsTab;
